import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";
import { selectTxnId, useMapStore } from "stores/map";
import formatPropertyName from "utils/column/formatPropertyName";
import {
  applyFieldNameMode,
  resolveSimplifyCustomFieldNames
} from "utils/column/getColumnLabelWithContext";

import useColumnsDictionary from "hooks/useColumnsDictionary";

import { EntityKind } from "models/entityKind";

import { applyRemoveUUID } from "components/charts/utils";
import { addNormalizationBasisToTitle } from "components/charts/utils/addNormalizationBasisToTitle";
import { useScreenshotContext } from "components/screenshot/hooks";
import { applyAbbreviations } from "components/screenshot/utils";
import { useUserSettings } from "components/user/hooks";

import { getChart } from "../charts/getChart";
import { useChartState } from "../context";

const useChartLegendTitle = (): string => {
  const chartState = useChartState();
  // state from store
  const txnId = useMapStore(selectTxnId);
  const globalGroupBy = useSelector((state: RootState) =>
    chartState.entityKind == EntityKind.Well
      ? state.groupBy.globalGroupBy
      : state.groupBy.globalFacilityFocus
  );

  const { userAbbreviations } = useUserSettings();
  const screenshotState = useScreenshotContext();
  const columnsDictionary = useColumnsDictionary(chartState.entityKind);

  // state
  const [value, setValue] = useState<string>("");

  const abbreviations = userAbbreviations?.abbreviations ?? [];

  useEffect(() => {
    const legendTitle =
      chartState.entityKind == EntityKind.Well
        ? txnId.legend.title
        : txnId.facilityLegend.title;
    const chart = getChart(chartState.settings.chartType);
    if (chart?.getCustomLegendTitle(chartState)) {
      setValue(chart.getCustomLegendTitle(chartState));
      return;
    }

    if (!legendTitle) {
      return;
    }

    const title = chartState.settings.useChartFocus
      ? chartState.settings.groupBy.title
      : globalGroupBy.title;

    const isForecastEnabled = title.includes("Forecast");

    let nextTitle = isForecastEnabled ? applyRemoveUUID(title) : title;

    // EVA-127 "Simplify Custom Field Names": single source of truth lives in
    // applyFieldNameMode. `undefined` (not in a chart screenshot) is
    // interpreted identically to OFF — subgroup-qualified for custom fields
    // (never the category — EVA-6338), decorated as usual for every other
    // type; ON strips down to `{field} ({unit})`.
    //
    // FIX 4: chart focus's group-by is sanitized (sanitizeObject via
    // DEFAULT_CHART_FOCUS_ENTITY's allow-list) before it's persisted on a
    // restored dashboard — it drops subgroup/defaultUnit/metadata, so
    // applyFieldNameMode can't correctly qualify or strip it. Re-resolve the
    // FULL column from columnsDictionary by property (same lookup
    // qualifyAxisName uses on the axis render path), so the legend matches
    // the axis for the same field. Falls back to the sanitized focus object
    // when the lookup misses (unresolved property, still-loading dictionary).
    // useColumnsDictionary also returns `{}` on the very first render (it
    // populates the dict in an effect, not during render), so that fallback
    // is also what renders on first paint — benign, since the effect re-runs
    // once columns load and this hook's own effect re-fires on the
    // `columnsDictionary` dependency below.
    const activeGroupBy = chartState.settings.useChartFocus
      ? (chartState.settings.groupBy?.property &&
          columnsDictionary?.[
            formatPropertyName(chartState.settings.groupBy.property)
          ]) ||
        chartState.settings.groupBy
      : globalGroupBy;
    const simplifyCustomFieldNames = resolveSimplifyCustomFieldNames(
      !!chartState.screenshot,
      screenshotState?.settings
    );
    // Must run BEFORE addNormalizationBasisToTitle below: applyFieldNameMode is
    // what appends the unit for a custom field (Phase 1 dropped it from the
    // stored title). addNormalizationBasisToTitle finds a known unit token
    // INSIDE the title and rewrites it in place — running it first found no
    // unit yet, so `String.replace(undefined, …)` no-op'd and the normalization
    // basis silently never appeared for a normalized custom field.
    nextTitle = applyFieldNameMode(nextTitle, activeGroupBy, simplifyCustomFieldNames);

    nextTitle = chartState.settings.useNormalizeBy
      ? addNormalizationBasisToTitle(nextTitle, chartState.settings.normalizeBy)
      : nextTitle;

    const formattedTitle =
      chartState.screenshot && screenshotState?.settings?.applyAbbreviations
        ? applyAbbreviations(nextTitle, abbreviations)
        : nextTitle;

    setValue(formattedTitle);
  }, [
    globalGroupBy,
    txnId.legend.title,
    chartState.settings?.otherProducts,
    chartState.settings?.useChartFocus,
    // FIX 5: the effect reads .property/.subgroup/.defaultUnit/.metadata off
    // this object (via the columnsDictionary lookup and applyFieldNameMode),
    // not just .title — depend on the object itself so two same-titled custom
    // fields in different subgroups (or a chart-focus switch) re-run this
    // effect. Matches the sibling hook (components/charts/legend/hooks/
    // useChartLegendTitle.tsx), which already depends on the whole object.
    chartState.settings?.groupBy,
    chartState.settings.useNormalizeBy,
    chartState.settings.normalizeBy,
    chartState.screenshot,
    screenshotState?.settings?.simplifyCustomFieldNames,
    columnsDictionary,
    abbreviations
  ]);

  return value;
};

export default useChartLegendTitle;
